/**
 * reviewCalculation.ts
 * 振り返り機能用の計算ユーティリティ
 * - 月別レートのMap構築
 * - 月別レートでの通貨変換
 * - 対象期間の集計（月別変換→合計）
 */
import Decimal from 'decimal.js';
import { convertCurrency } from './CurrencyConverter';
import type { Currency, MonthlyRate } from '../contexts/types';

/**
 * 通貨変換関数の型。
 * 通常モード: convertCurrency を直接使用（yearMonth 未指定）
 * 振り返りモード: 月別レートを使用する convertWithRate を渡す（yearMonth 指定時）
 *
 * calculation.ts 等の共通ユーティリティにオプショナル引数として渡すことで、
 * 既存の呼び出し元に影響を与えずに月別レート対応が可能。
 */
export type ConvertFn = (
  amount: number | Decimal,
  from: string,
  to: string,
  yearMonth?: string
) => Decimal;

/**
 * APIレスポンス（MonthlyRate[]）を Map<月, Currency[]> に変換する
 * 振り返りモード突入時に1回だけ実行し、以降のルックアップをO(1)にする
 *
 * @param monthlyRates getCurrencyByPeriod APIのレスポンス
 * @returns Map<"2025-04", Currency[]>
 */
export function buildMonthlyRateMap(monthlyRates: MonthlyRate[]): Map<string, Currency[]> {
  const map = new Map<string, Currency[]>();
  for (const r of monthlyRates) {
    // rateMonth: "2025-04-01" → "2025-04"
    const key = r.rateMonth.substring(0, 7);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push({
      currencyId: r.currencyId,
      currencyName: r.currencyName,
      exchangeRate: r.exchangeRate,
    });
  }
  return map;
}

/**
 * 指定月のレートを Currency[] 形式で取得する
 * フォールバック: 対象月なし → 直近過去月 → fallbackRates
 *
 * TODO: 振り返り時点以降は「申請時レート」にフォールバックする方針に変更する可能性あり
 *   現在: rateMap になければ直近過去月 → それも無ければ申請時レート
 *   将来案: yearMonth > reviewDate なら直接申請時レートを使用（計画値との差分を明確に分離）
 *
 * @param yearMonth 対象月 "YYYY-MM"
 * @param rateMap 月別レートMap（buildMonthlyRateMapの結果）
 * @param fallbackRates フォールバック用レート（T_CEAS_CURRENCYのプロジェクト保存レート）
 * @returns Currency[]
 */
export function getRatesForMonth(
  yearMonth: string,
  rateMap: Map<string, Currency[]>,
  fallbackRates?: Currency[]
): Currency[] {
  // 対象月にデータあり
  const rates = rateMap.get(yearMonth);
  if (rates && rates.length > 0) return rates;

  // 対象月になし → 直近の過去月を探す
  // （振り返り時点以降はレートなし → reviewDate 付近のレートが延長適用される）
  const sortedKeys = Array.from(rateMap.keys())
    .filter(k => k < yearMonth)
    .sort()
    .reverse();

  if (sortedKeys.length > 0) {
    const nearestRates = rateMap.get(sortedKeys[0]);
    if (nearestRates && nearestRates.length > 0) return nearestRates;
  }

  // 過去月もなし → フォールバック
  return fallbackRates ?? [];
}

/**
 * 月別レートで通貨変換する
 * 内部で getRatesForMonth → 既存の convertCurrency を呼ぶ
 *
 * @param amount 変換する値
 * @param sourceCurrency 元の通貨名（"USD" 等）
 * @param targetCurrency 変換先の通貨名（"JPY" 等）
 * @param yearMonth 対象月 "YYYY-MM"
 * @param rateMap 月別レートMap
 * @param fallbackRates フォールバック用レート
 * @returns 変換後の金額（Decimal）
 */
export function convertCurrencyByMonth(
  amount: number | Decimal,
  sourceCurrency: string,
  targetCurrency: string,
  yearMonth: string,
  rateMap: Map<string, Currency[]>,
  fallbackRates?: Currency[]
): Decimal {
  if (sourceCurrency === targetCurrency) return new Decimal(amount);
  const rates = getRatesForMonth(yearMonth, rateMap, fallbackRates);
  return convertCurrency(amount, sourceCurrency, targetCurrency, rates);
}

/**
 * 月次データを対象期間で集計する（月別レート変換付き）
 * 各月を個別にレート変換してから合計する
 *
 * @param monthlyData 月次データ { "2025-04": 100, "2025/04": 100, ... }
 * @param sourceCurrency 元の通貨名
 * @param targetCurrency 変換先の通貨名
 * @param periodStart 開始月 "YYYY-MM"
 * @param periodEnd 終了月 "YYYY-MM"
 * @param rateMap 月別レートMap
 * @param fallbackRates フォールバック用レート
 * @returns 変換後の合計値（Decimal）
 */
export function sumByPeriodWithRate(
  monthlyData: Record<string, number>,
  sourceCurrency: string,
  targetCurrency: string,
  periodStart: string,
  periodEnd: string,
  rateMap: Map<string, Currency[]>,
  fallbackRates?: Currency[]
): Decimal {
  let total = new Decimal(0);
  const months = getMonthRange(periodStart, periodEnd);

  for (const month of months) {
    // 月次データのキー形式に対応（"YYYY-MM" or "YYYY/MM"）
    const value = monthlyData[month] ?? monthlyData[toSlashFormat(month)] ?? 0;
    if (value === 0) continue;

    const converted = convertCurrencyByMonth(
      value, sourceCurrency, targetCurrency, month, rateMap, fallbackRates
    );
    total = total.add(converted);
  }

  return total;
}

/**
 * "YYYY-MM" 〜 "YYYY-MM" の月配列を生成する
 *
 * @param start 開始月 "YYYY-MM"
 * @param end 終了月 "YYYY-MM"
 * @returns ["2025-04", "2025-05", ..., "2025-12"]
 */
export function getMonthRange(start: string, end: string): string[] {
  const months: string[] = [];
  let [year, month] = start.split('-').map(Number);
  const [endYear, endMonth] = end.split('-').map(Number);

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month++;
    if (month > 12) { month = 1; year++; }
  }
  return months;
}

/**
 * "YYYY-MM" → "YYYY/MM" 変換（既存データのキー形式に合わせる）
 */
function toSlashFormat(yearMonth: string): string {
  return yearMonth.replace('-', '/');
}
